import type { RequestFn } from '../http';

/**
 * Anwesenheit (v1.405.0): Einstempeln, Pause, Ausstempeln im ServiceHub — danach an Jibble.
 * Backend: services/attendance/, routes/attendance.ts.
 *
 * Der Knopf schickt keine Uhrzeit: Stempelzeit ist die Serverzeit. `clientRef` ist eine ID je
 * gemeinter Aktion — ein Doppeltipp oder ein Neuversuch nach Netzfehler legt damit keinen zweiten
 * Stempel an.
 */

export type PunchType = 'IN' | 'BREAK_START' | 'BREAK_END' | 'OUT';
export type AttendanceState = 'OFF' | 'WORKING' | 'ON_BREAK';
export type JibbleStatus = 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED' | 'MANUAL';

export interface MyPunch {
  id: string;
  type: PunchType;
  at: string;
  source: 'APP' | 'ON_CALL' | 'ADMIN' | 'JIBBLE';
  jibbleStatus: JibbleStatus;
}

export interface MyAttendance {
  state: AttendanceState;
  since: string | null;
  serverTime: string;
  today: {
    date: string;
    workMinutes: number;
    breakMinutes: number;
    firstIn: string | null;
    punches: MyPunch[];
  };
  jibbleEnabled: boolean;
  /** Stand in Jibble (alle 5 Min. gelesen, v1.415.0); null = noch nicht gelesen */
  jibble: {
    state: AttendanceState;
    since: string | null;
    checkedAt: string;
    /** Jibble sagt etwas anderes als der ServiceHub (z. B. dort direkt gestempelt) */
    differs: boolean;
  } | null;
}

export interface PunchResponse {
  created: boolean;
  punch: { id: string; type: PunchType; at: string; jibbleStatus: JibbleStatus };
  status: MyAttendance;
  /** Hinweis, wenn Jibble vor dem Stempel etwas anderes sagte (null = nichts zu sagen) */
  jibbleNotice?: string | null;
}

/** Nur für ADMIN und TEAMLEAD — der Server liefert das Feld Technikern gar nicht. */
export interface TeamMemberDetails {
  role: string;
  loggedInSince: string | null;
  loggedInMinutes: number | null;
  lastActivity: string | null;
  online: boolean;
  activeSessions: number;
  devices: string[];
  workMinutesToday: number;
  breakMinutesToday: number;
  firstInToday: string | null;
  punchesToday: MyPunch[];
}

export interface TeamMember {
  userId: string;
  name: string;
  color: string | null;
  state: AttendanceState;
  since: string | null;
  details?: TeamMemberDetails;
}

export interface TeamNow {
  members: TeamMember[];
  counts: Record<AttendanceState, number>;
  detailed: boolean;
  serverTime: string;
}

export interface AdminPunch {
  id: string;
  userId: string;
  userName: string | null;
  type: PunchType;
  at: string;
  source: 'APP' | 'ON_CALL' | 'ADMIN' | 'JIBBLE';
  note: string | null;
  reason: string | null;
  createdByUserId: string | null;
  replacesPunchId: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  jibble: {
    status: JibbleStatus;
    entryId: string | null;
    attempts: number;
    lastError: string | null;
    sentAt: string | null;
    nextAttemptAt: string | null;
  };
}

export interface TeamDayRow {
  userId: string;
  name: string;
  color: string | null;
  workMinutes: number;
  breakMinutes: number;
  firstIn: string | null;
  lastOut: string | null;
  loggedInMinutes: number;
  firstLogin: string | null;
  punches: AdminPunch[];
  logins: Array<{ kind: 'LOGIN' | 'LOGOUT' | 'EXPIRED' | 'REVOKED' | 'EVICTED'; at: string; device: string }>;
}

export const ATTENDANCE_ME_QUERY_KEY = ['attendance', 'me'] as const;
export const ATTENDANCE_TEAM_QUERY_KEY = ['attendance', 'team'] as const;
export const ATTENDANCE_DAY_QUERY_KEY = ['attendance', 'day'] as const;
export const ATTENDANCE_JIBBLE_QUEUE_QUERY_KEY = ['attendance', 'jibble-queue'] as const;

export function createAttendanceApi(request: RequestFn) {
  return {
    async getMyAttendance(): Promise<MyAttendance> {
      return request<MyAttendance>('/attendance/me');
    },
    async punchAttendance(type: PunchType, clientRef: string): Promise<PunchResponse> {
      return request<PunchResponse>('/attendance/punch', {
        method: 'POST',
        body: JSON.stringify({ type, clientRef }),
      });
    },
    async getTeamNow(): Promise<TeamNow> {
      return request<TeamNow>('/attendance/team');
    },
    async getTeamDay(date: string): Promise<{ date: string; rows: TeamDayRow[] }> {
      return request<{ date: string; rows: TeamDayRow[] }>(`/attendance/day?date=${encodeURIComponent(date)}`);
    },
    async getAttendanceJibbleQueue(): Promise<{ punches: AdminPunch[]; counts: Record<'PENDING' | 'FAILED' | 'MANUAL', number> }> {
      return request('/admin/attendance/jibble-queue');
    },
    async addAttendancePunch(body: { userId: string; type: PunchType; date: string; time: string; reason: string; clientRef?: string }): Promise<{ punchId: string }> {
      return request<{ punchId: string }>('/admin/attendance/punches', { method: 'POST', body: JSON.stringify(body) });
    },
    async correctAttendancePunch(id: string, body: { date: string; time: string; type?: PunchType; reason: string }): Promise<{ punchId: string; jibbleStatus: JibbleStatus }> {
      return request(`/admin/attendance/punches/${id}/correct`, { method: 'POST', body: JSON.stringify(body) });
    },
    async voidAttendancePunch(id: string, reason: string): Promise<{ punchId: string; jibbleStatus: JibbleStatus }> {
      return request(`/admin/attendance/punches/${id}/void`, { method: 'POST', body: JSON.stringify({ reason }) });
    },
    async resolveAttendanceJibble(id: string, action: 'retry' | 'acknowledge', note?: string): Promise<{ punchId: string; jibbleStatus: JibbleStatus; lastError: string | null }> {
      return request(`/admin/attendance/punches/${id}/jibble`, { method: 'POST', body: JSON.stringify(note ? { action, note } : { action }) });
    },
  };
}
